import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { Box, CircleButton, withStyles } from 'vopay';
import styles from './EditDetailItem.module.scss';

const cn = withStyles(styles);

type EditDetailItemProps = {
  value: ReactNode;
  children: ReactNode;
  onActivate: () => void;
  onCancel: () => void;
  onUpdate: (values: Record<string, string>) => Promise<void>;
};

export default function EditDetailItem({ value, children, onActivate, onCancel, onUpdate }: EditDetailItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: FormEvent<HTMLFormElement>) {
    try {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      // These forms only contain text inputs, so every entry is a string.
      const entries = Object.fromEntries(Array.from(formData.entries(), ([key, value]) => [key, String(value)]));
      await onUpdate(entries);
      setIsEditing(false);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
    }
  }

  function activate() {
    onActivate();
    setIsEditing(true);
    const controller = new AbortController();

    window.addEventListener(
      'keydown',
      (e) => {
        if (e.code === 'Escape') {
          cancel();
          controller.abort();
        }
      },
      { signal: controller.signal },
    );
  }

  function cancel() {
    setIsEditing(false);
    onCancel();
  }

  useEffect(() => {
    setError('');
  }, [isEditing]);

  if (isEditing) {
    return (
      <div>
        <form className={cn('editDetailItem')} onSubmit={submit}>
          {children}

          <Box ai="center" gapSize="8px">
            <CircleButton colorScheme="destructive" icon="close" size="large" onClick={cancel} />
            <CircleButton type="submit" colorScheme="success" icon="check" size="large" />
          </Box>
        </form>

        {error && <span className={cn('error')}>{error}</span>}
      </div>
    );
  }

  return (
    <div className={cn('editDetailItem')}>
      <div>{value}</div>
      <CircleButton colorScheme="action" icon="edit" onClick={activate} />
    </div>
  );
}
